import { ehEscritorio } from '../../utils/escopoUsuario';
import { primeiraRotaDoUsuario } from '../menu-itens';

// =====================================================================
// HOME — quais ABAS o usuário alcança, e em qual delas a Home abre.
// =====================================================================
// FONTE ÚNICA da regra. A Home (desktop e mobile) e a barra de abas leem daqui;
// nenhum dos dois escreve nome de perfil à mão.
//
// Existe desde 11/09/2026, quando as DUAS Homes viraram UMA (Julia): antes havia
// um `if` no meio do render do `Home.js` escolhendo entre o `DashboardOperacao`
// (perfis de gestão) e a "home genérica" (todo o resto), e a barra Operação|RH
// pertencia ao próprio DashboardOperacao. Com três abas, quem decide em que aba
// se está passou a ser a PÁGINA, e a regra precisou de um dono.
//
// ⚠️⚠️ ISTO É REGRA DE ACESSO, e ela NÃO FALHA COM ERRO — falha em silêncio, dos
//    dois lados. Errar para menos: a aba some e a pessoa perde o painel sem
//    nenhuma mensagem. Errar para mais: a aba aparece e MOSTRA o dado, porque as
//    três rotas do dashboard (`/api/home/operacao/vendas`, `/operacao/avaliacoes`
//    e `/rh/painel`, em `server/home/server-home.js`) têm só `authenticateToken`
//    — não existe `authorizeRoles` nelas. Quem restringe hoje é ESTE arquivo.
//    Por isso há teste nomeado ao lado (`abas-home.test.js`).
//
// ⚠️ E o recorte por loja NÃO é a rede de segurança que parece: o `escopoLojaSql`
//    isenta quem é ESCRITÓRIO. Uma aba Operação dada por engano a um perfil de
//    escritório entrega venda, orçamento e projeção da REDE INTEIRA.

// ⚠️⚠️ TEMPORÁRIO — A ABA ESCRITÓRIO ESTÁ FECHADA A UM ÚNICO USUÁRIO.
//    Julia (ID 57) pediu isso em 11/09/2026, no dia em que o painel de Atividades
//    Pendentes ganhou 17 cards: os números ainda estão sendo conferidos um a um
//    (dois já estavam errados — Primeiros 15 contava 241 por ignorar o formulário
//    pontuado, e o Petty Cash contava despesa onde a unidade é a loja), e um
//    painel de cobrança que mostra número errado queima a confiança da rede de
//    uma vez só. Melhor fechado do que desacreditado.
//
// ⚠️ A CONSEQUÊNCIA precisa ser dita: a régua `ehEscritorio` continua escrita
//    abaixo e é a regra DE VERDADE — esta lista está por cima dela, como filtro
//    temporário. **Apagar `ESCRITORIO_LIBERADO_PARA` (ou deixá-lo vazio)
//    devolve a aba a todo o escritório**, que é o estado pretendido. É assim de
//    propósito: a restrição tem de ser fácil de remover, não de esquecer.
//
// ⚠️ Enquanto isto vale, MUITO perfil de escritório fica SEM NENHUMA aba
//    (Financeiro, Fiscal, Contábil, Compras, Manutenção, Gerente TI, Marketing,
//    Médico, Treinamento, Logística…). Eles não podem cair numa Home vazia — é
//    para isso que existe a `rotaInicialDoUsuario` no fim deste arquivo.
const ESCRITORIO_LIBERADO_PARA = [57];

export const ABA_OPERACAO = 'operacao';
export const ABA_DESEMPENHO = 'desempenho';
export const ABA_RH = 'rh';
export const ABA_ESCRITORIO = 'escritorio';

// Perfis que alcançam a visão OPERAÇÃO (venda, orçamento, projeção, avaliações).
// ⚠️ `Diretoria Op` entrou em 11/09/2026 a pedido da Julia. Ele é PERFIL DERIVADO
//    (herda de `Diretoria` — ver `src/utils/perfisDerivados.js`), mas a Home não
//    passa pela derivação: ela compara o perfil cru, e por isso a diretoria DA
//    OPERAÇÃO caía na home genérica. Está escrito aqui, por extenso, justamente
//    para não depender de uma derivação que esta tela não aplica.
// ⚠️ `Operacional` entrou em 28/09/2026 a pedido da Julia: papel de operação da
//    rede, deve ver Operação (e RH, via `PERFIS_RH` que deriva desta lista). Como
//    esse perfil é de ESCRITÓRIO, essas abas mostram a REDE INTEIRA — confirmado
//    como o pretendido. Antes ele só tinha a aba Desempenho (`PERFIS_DESEMPENHO`).
const PERFIS_OPERACAO = ['Admin', 'Gerente', 'Diretoria', 'Diretoria Op', 'Gerente Plantao', 'Operacional'];

// Perfis que alcançam a aba DESEMPENHO (Painel "Desempenho dos Restaurantes",
// DEM-00012, PDF "FORMA DE AVALIAR E CLASSIFICAR"). O `Gerente` entrou em
// 30/09/2026 (saiu do teste a pedido da Julia); no backend a resposta dele é
// RECORTADA pela própria loja (`escopoLojaSql`), então vê só o próprio
// restaurante, enquanto os perfis de escritório veem a rede inteira.
// ⚠️ `Gerente Plantao` NÃO entrou — o pedido foi só do perfil `Gerente`.
// `Presidencia` fica fora por outro motivo: é REDUÇÃO DE ALCANCE dela
// (`src/utils/perfisRestritos.js` — só Projetos, "sem Home"), não uma decisão
// desta demanda. ⚠️ ESPELHA `PERFIS_PAINEL_DESEMPENHO` do backend
// (`server/home/server-home.js`) — divergir dá aba sem dado (a rota nega) ou
// dado sem aba (a rota deixa passar e ninguém trava na tela).
const PERFIS_DESEMPENHO = ['Admin', 'Diretoria', 'Diretoria Op', 'Operacional', 'Gerente'];

// Perfis que alcançam a visão RH (quadro, absenteísmo). Quem vê Operação vê RH;
// `RH` e `Diretoria RH` veem SÓ esta — é o recorte que os define.
const PERFIS_RH = [...PERFIS_OPERACAO, 'RH', 'Diretoria RH'];

// Comparação em minúsculas, como o `authorizeRoles` do backend: tolera a grafia
// antiga de um cadastro velho sem virar licença para escrever errado em código novo.
const temPerfil = (lista, perfil) => lista.map((p) => p.toLowerCase()).includes((perfil || '').toLowerCase());

/**
 * As abas deste usuário, NA ORDEM em que aparecem na barra.
 *
 * ⚠️ A régua da aba Escritório é a LOJA do cadastro (`ehEscritorio`), não uma
 *    lista de perfis: é a mesma que o backend usa para decidir quem enxerga a
 *    rede. Gerente de loja, portanto, não tem aba Escritório — e isso é o
 *    desenho, não esquecimento (decisão da Julia, 11/09: os cards que lhe
 *    serviriam ou já estão nas outras abas, ou vão ser reformulados).
 *
 * ⚠️ Pode voltar VAZIO — perfil restrito (`TI`, `Presidencia`,
 *    `Coordenador de Qualidade`) sequer chega na Home, é barrado antes pelo
 *    `App.js`. A Home não deve quebrar se chegar assim mesmo.
 */
export function abasDoUsuario(user) {
    const perfil = user?.perfil;
    const abas = [];
    if (temPerfil(PERFIS_OPERACAO, perfil)) abas.push(ABA_OPERACAO);
    if (temPerfil(PERFIS_DESEMPENHO, perfil)) abas.push(ABA_DESEMPENHO);
    if (temPerfil(PERFIS_RH, perfil)) abas.push(ABA_RH);
    // ⚠️ A régua É `ehEscritorio` (a LOJA do cadastro, espelhando o backend); a
    //    lista de IDs é um portão TEMPORÁRIO por cima dela. Ver o comentário de
    //    ESCRITORIO_LIBERADO_PARA — tirar a lista devolve a aba ao escritório todo.
    const liberadoPorUsuario = !ESCRITORIO_LIBERADO_PARA.length
        || ESCRITORIO_LIBERADO_PARA.includes(Number(user?.id));
    if (ehEscritorio(user) && liberadoPorUsuario) abas.push(ABA_ESCRITORIO);
    return abas;
}

/**
 * Em que aba a Home abre.
 *
 * Mantém a rotina de hoje (decisão da Julia, 11/09): quem já caía no dashboard
 * continua caindo nele; quem caía na home genérica abre em Escritório. Como a
 * ordem de `abasDoUsuario` é Operação · RH · Escritório, "a primeira que ele
 * tem" JÁ é essa regra — não precisa de uma segunda tabela para dizer o mesmo,
 * que é exatamente o tipo de par que sai de sincronia.
 */
export function abaInicial(user) {
    return abasDoUsuario(user)[0] || null;
}

/**
 * Para onde mandar quem chegou na Home sem NENHUMA aba.
 *
 * ⚠️⚠️ Isto não é enfeite: com a aba Escritório fechada ao ID 57, a maioria dos
 *    perfis de escritório fica com zero aba. Sem este desvio eles veriam uma Home
 *    em branco — sem erro, sem mensagem, sem pista de que o portal está inteiro.
 *
 * O destino é o **primeiro módulo do MENU DELE** (`primeiraRotaDoUsuario`, do
 * Sidebar), e não uma tabela de landing escrita aqui. ⚠️ Uma segunda tabela
 * divergiria do menu em silêncio — mandaria a pessoa para um módulo que ela não
 * alcança, e o portão de rota do App.js a devolveria para a Home, que a mandaria
 * de novo para lá: laço. Derivando do menu, o destino é sempre algo que ela abre.
 *
 * `null` = o usuário não alcança módulo nenhum. Quem chama NÃO deve redirecionar
 * nesse caso (não há para onde) — mostre uma mensagem.
 */
export function rotaInicialDoUsuario(user, caps = new Set()) {
    // ⚠️ `caps` entrou em 18/09/2026 e NÃO é opcional na prática: sem ele, um
    //    módulo governado por capacidade (hoje o Chamados) nunca pode ser o
    //    destino — e quem só alcança esse módulo ficaria numa Home em branco,
    //    sem erro e sem pista. É a mesma armadilha que o comentário acima
    //    descreve para uma "segunda tabela de landing": a divergência é muda.
    return primeiraRotaDoUsuario(user, caps);
}
